/**
 * The ActivityDetails-Component displays a specific activity
 * along with its completion and waiting information.
 */
import React from "react";
import { Grid, Paper, Typography, useTheme, styled } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import ActivityStatusChip from "../../components/activities/ActivityStatusChip";
import { getActivityResultTypeLabel, getActivityTypeLabel } from "../../components/activities/activityLabels";
import useActivityDetails from "../../hooks/activities/useActivityDetails";
import { ActivityStatus } from "../../types/activityTypes";

const StyledLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline;
  }
`;

const ActivityDetails: React.FunctionComponent = () => {
  const theme = useTheme();
  const params = useParams();
  const { id: contactPersonId, activityId } = (params || {}) as { id?: string; activityId?: string };
  const resolvedActivityId = Number(activityId || contactPersonId);
  const fromContactPersonId = contactPersonId && activityId ? Number(contactPersonId) : null;
  const { activity, isLoading } = useActivityDetails(resolvedActivityId);
  const isWaitingForCustomer = activity?.status === ActivityStatus.WAITING_FOR_CUSTOMER;
  const isDone = activity?.status === ActivityStatus.DONE;
  const hasWaitingComment = Boolean(activity?.waitingComment?.trim());

  const styles = {
    section: {
      padding: "20px",
      marginBottom: "24px",
    },
    labelCell: {
      width: "220px",
    },
    sectionTitle: {
      marginBottom: "16px",
      fontWeight: 700,
    },
    commentBox: {
      minHeight: "120px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "6px",
      padding: "16px",
      backgroundColor: theme.palette.background.paper,
      whiteSpace: "pre-wrap",
    },
  };

  if (isLoading) {
    return <Typography>Aktivität wird geladen...</Typography>;
  }

  if (!activity) {
    return <Typography>Aktivität nicht gefunden.</Typography>;
  }

  return (
    <div>
      <Paper sx={styles.section}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          Aktivitätsdetails
        </Typography>
        <Grid container spacing={1}>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Titel:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{activity.title}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Beschreibung:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{activity.description || "-"}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Typ:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{getActivityTypeLabel(activity.activityType)}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Status:</Typography>
            </Grid>
            <Grid item xs>
              <ActivityStatusChip status={activity.status} />
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Ansprechperson:</Typography>
            </Grid>
            <Grid item xs>
              <Typography color="secondary">
                <StyledLink to={`/ansprechpersonen/${activity.contactPersonId}`}>
                  {activity.contactPersonName}
                </StyledLink>
              </Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Unternehmen:</Typography>
            </Grid>
            <Grid item xs>
              <Typography color="secondary">
                <StyledLink to={`/unternehmen/${activity.companyId}`}>{activity.companyName}</StyledLink>
              </Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Verantwortliche Person:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{activity.responsibleUserName}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Deadline:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{dayjs(activity.deadline).format("DD.MM.YYYY")}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Erstellt von:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{activity.createdByName}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Erstellt am:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{dayjs(activity.createdAt).format("DD.MM.YYYY HH:mm")}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Zuletzt geändert:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{dayjs(activity.updatedAt).format("DD.MM.YYYY HH:mm")}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item sx={styles.labelCell}>
              <Typography variant="body1">Wiederkehrend:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{activity.isRecurring ? "Ja" : "Nein"}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {(isDone || isWaitingForCustomer || fromContactPersonId) && (
        <Paper sx={styles.section}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            {isWaitingForCustomer ? "Warten auf Kunde" : "Abschluss"}
          </Typography>
          <Grid container spacing={1}>
            {isDone && (
              <>
                {hasWaitingComment && (
                  <Grid item xs={12}>
                    <Typography variant="body1" marginBottom="8px">
                      Warte-Grund:
                    </Typography>
                    <Paper variant="outlined" sx={styles.commentBox}>
                      <Typography>{activity.waitingComment}</Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item container xs={12}>
                  <Grid item sx={styles.labelCell}>
                    <Typography variant="body1">Ergebnis:</Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography>
                      {activity.resultType ? getActivityResultTypeLabel(activity.resultType) : "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container xs={12}>
                  <Grid item sx={styles.labelCell}>
                    <Typography variant="body1">Abgeschlossen am:</Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography>
                      {activity.completedAt ? dayjs(activity.completedAt).format("DD.MM.YYYY HH:mm") : "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" marginBottom="8px">
                    Kommentar:
                  </Typography>
                  <Paper variant="outlined" sx={styles.commentBox}>
                    <Typography>{activity.resultComment || "-"}</Typography>
                  </Paper>
                </Grid>
              </>
            )}
            {isWaitingForCustomer && hasWaitingComment && (
              <Grid item xs={12}>
                <Typography variant="body1" marginBottom="8px">
                  Wartet auf:
                </Typography>
                <Paper variant="outlined" sx={styles.commentBox}>
                  <Typography>{activity.waitingComment}</Typography>
                </Paper>
              </Grid>
            )}
            {fromContactPersonId && (
              <Grid item xs={12}>
                <Typography color="secondary">
                  <StyledLink to={`/ansprechpersonen/${fromContactPersonId}`}>Zurück zur Ansprechperson</StyledLink>
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
    </div>
  );
};

export default ActivityDetails;
